// BarChart.js
import moment from 'moment';
import * as d3 from 'd3';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { Spinner, ContextMenu } from '@blueprintjs/core';

import { ItemContext } from '../App';
import { RiskItemType } from './itemDefinitions';

const formatValue = d3
  .formatLocale({
    decimal: ',',
    thousands: '\u00a0',
    grouping: [3],
    currency: ['', '\u00a0€']
  })
  .format('$.3~s');

const ScatterChart = ({ height, width, margin, data, inHeight, inWidth, simInfo }) => {
  const { filterDict } = useContext(ItemContext);
  const filteredData = data.filter((d) => d.block === filterDict.currentBlock);
  const ref = useRef();

  let line;
  let x;
  let y;

  const draw = () => {
    d3.select(ref.current).selectAll('*').remove();

    const svg = d3
      .select(ref.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    // Add X axis
    x = d3
      .scaleLinear()
      .domain([
        d3.min(filteredData, function (d) {
          return +d.value;
        }),
        d3.max(filteredData, function (d) {
          return +d.value;
        })
      ])
      .range([0, inWidth])
      .nice();

    const g = svg.append('g').style('font', '16px arial').style('font-weight', 'bold');
    g.attr('transform', 'translate(0,' + inHeight + ')').call(d3.axisBottom(x).ticks(8, '~s'));

    // Add Y axis
    y = d3.scaleLinear().domain([0, 100]).range([inHeight, 0]);

    const g2 = svg.append('g').style('font', '16px arial').style('font-weight', 'bold');
    g2.attr('transform', 'translate(0,0)').call(d3.axisLeft(y).tickFormat((y) => `${y}%`));

    // create a tooltip
    const Tooltip = svg
      .append('text')
      .attr('x', 0)
      .attr('y', 0)
      .style('opacity', 0)
      .style('font-size', 17);

    // What happens when the mouse move -> show the annotations at the right positions.
    function mouseover() {
      focus.style('opacity', 1);
      focusText.style('opacity', 1);
    }

    const format = formatValue;

    function mousemove(event) {
      // recover coordinate we need
      const x0 = x.invert(d3.pointer(event)[0]);
      const i = bisect(filteredData, x0);
      const selectedData = filteredData[i];
      if (selectedData !== undefined) {
        focus.attr('cx', x(selectedData.value)).attr('cy', y(selectedData.perc));
        focusText
          .html(formatValue(selectedData.value) + ' -  ' + selectedData.perc + '%')
          .attr('x', x(selectedData.value) + 15)
          .attr('y', y(selectedData.perc));
      }
    }
    function mouseout() {
      focus.style('opacity', 0);
      focusText.style('opacity', 0);
    }

    const openLink = (d, i) => {
      console.log(i);
    };

    // This allows to find the closest X index of the mouse:
    const bisect = d3.bisector(function (d) {
      return d.value;
    }).left;

    // Create the circle that travels along the curve of chart
    const focus = svg
      .append('g')
      .append('circle')
      .style('fill', 'none')
      .attr('stroke', 'black')
      .attr('r', 8.5)
      .style('opacity', 0);

    // Create the text that travels along the curve of chart
    const focusText = svg
      .append('g')
      .append('text')
      .style('opacity', 0)
      .attr('text-anchor', 'left')
      .attr('alignment-baseline', 'middle');

    // TODO move before rect that collects mouse location
    line = svg
      .append('g')
      .append('path')
      .datum(filteredData)
      .attr(
        'd',
        d3
          .line()
          .curve(d3.curveBasis)
          .x(function (d) {
            return x(d.value);
          })
          .y(function (d) {
            return y(d.perc);
          })
      )
      .attr('fill', 'none')
      .attr('stroke', '#0E5A8A')
      .attr('stroke-width', 4);

    // Create a rect on top of the svg area: this rectangle recovers mouse position
    svg
      .append('rect')
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .attr('width', width)
      .attr('height', height)
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseout', mouseout);
  };

  // TODO doesnt work atm
  useEffect(() => {
    const newData = data.filter((d) => d.block === filterDict.currentBlock);
    if (line !== undefined) {
      line
        .datum(newData)
        .transition()
        .duration(100)
        .attr(
          'd',
          d3
            .line()
            .curve(d3.curveBasis)
            .x(function (d) {
              return x(d.value);
            })
            .y(function (d) {
              return y(d.perc);
            })
        )
        .attr('fill', 'none')
        .attr('stroke', '#5C7080')
        .attr('stroke-width', 4);
    }
  }, [filterDict.currentBlock]);

  useEffect(() => {
    draw();
  }, [height, width, margin, data, inHeight, inWidth]);

  return (
    <div className="chart">
      <svg ref={ref} />
    </div>
  );
};

const SimResultItems = ({ itemProps }) => {
  const [height, setHeight] = useState(window.innerHeight - 160);
  const [width, setWidth] = useState(window.innerWidth);
  const [data, setData] = useState([]);

  const { filterDict } = useContext(ItemContext);

  const margin = { top: 50, right: 30, bottom: 40, left: 50 };
  const inWidth = width - margin.left - margin.right;
  const inHeight = height - margin.top - margin.bottom;

  const itemType = RiskItemType.SimResultSheet;

  /** Update the size of the graph element - with a short delay to avoid too many renders */
  const updateGraphSize = () => {
    setTimeout(() => {
      if (height !== window.innerHeight - 100) {
        setHeight(window.innerHeight - 100);
      }
      if (width !== window.innerWidth) {
        setWidth(window.innerWidth);
      }
    }, 200);
  };

  window.addEventListener('resize', updateGraphSize);

  useEffect(() => {
    if (itemProps[itemType].filteredItems[0] && itemProps[itemType].filteredItems[0].results)
      setData(itemProps[itemType].filteredItems[0].results);
    else {
      setData([]);
    }
  }, [itemProps[itemType].filteredItems]);

  const chartProps = {
    height,
    width,
    margin,
    inHeight,
    inWidth,
    data,
    simInfo: itemProps[itemType].filteredItems[0]
  };

  if (data.length === 0) {
    return (
      <div>
        <Spinner className="centered" size={100} />
      </div>
    );
  }

  return (
    <div>
      <ScatterChart {...chartProps} />
      <div>
        Yksityiskohdat simulaation tuloksista löydät{' '}
        <a
          href={`https://docs.google.com/spreadsheets/d/${itemProps[itemType].filteredItems[0].id}/`}
          target="_blank"
          rel="noopener noreferrer"
        >
          täältä
        </a>
        .
      </div>
    </div>
  );
};

export default SimResultItems;
